import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalyticalInsightsPanel = () => {
  const [expandedInsight, setExpandedInsight] = useState(null);

  const insights = [
    {
      id: 1,
      type: 'recommendation',
      priority: 'high',
      title: 'Optimal Hiring Window Detected',
      summary: 'March-April shows 32% higher placement success rates',
      details: 'Historical data indicates that recruitment drives during March-April result in 32% higher placement success rates compared to other months. This aligns with academic calendar completion and increased employer hiring budgets.',
      action: 'Schedule recruitment campaigns for March 2026',
      icon: 'Calendar',
      color: 'var(--color-success)'
    },
    {
      id: 2,
      type: 'anomaly',
      priority: 'medium',
      title: 'Unusual Decline in Finance Sector Applications',
      summary: '18% drop in finance applications detected this month',
      details: 'Finance sector applications have decreased by 18% compared to the 6-month average. This may indicate shifting student preferences or external market factors affecting the banking industry.',
      action: 'Investigate finance sector trends and student preferences',
      icon: 'AlertTriangle',
      color: 'var(--color-warning)'
    },
    {
      id: 3,
      type: 'benchmark',
      priority: 'high',
      title: 'Above Industry Average Performance',
      summary: 'Your placement rate exceeds industry benchmark by 12%',
      details: 'Current placement rate of 87.5% is 12% higher than the national average of 75.5%. This positions your institution in the top 15% of placement performance across similar institutions.',
      action: 'Leverage this metric in marketing materials',
      icon: 'Award',
      color: 'var(--color-primary)'
    },
    {
      id: 4,
      type: 'recommendation',
      priority: 'medium',
      title: 'Skill Gap Opportunity in Cloud Computing',
      summary: 'High demand for cloud skills with limited candidate supply',
      details: 'Employer demand for cloud computing skills has increased 45% while only 23% of students have relevant certifications. Bridging this gap could improve placement rates by an estimated 15%.',
      action: 'Introduce cloud computing training programs',
      icon: 'Cloud',
      color: 'var(--color-accent)'
    },
    {
      id: 5,
      type: 'anomaly',
      priority: 'low',
      title: 'Seasonal Pattern Deviation',
      summary: 'Interview scheduling 25% below seasonal average',
      details: 'Current interview scheduling volume is 25% below the typical seasonal pattern for this time of year. This could impact placement timelines if not addressed.',
      action: 'Review employer engagement strategies',
      icon: 'TrendingDown',
      color: 'var(--color-error)'
    },
    {
      id: 6,
      type: 'benchmark',
      priority: 'high',
      title: 'Salary Competitiveness Analysis',
      summary: 'Average package 8% above regional benchmark',
      details: 'Your average placement package of ₹7.2 LPA is 8% higher than the regional average of ₹6.67 LPA, indicating strong employer quality and student competitiveness.',
      action: 'Highlight salary competitiveness to prospective students',
      icon: 'DollarSign',
      color: 'var(--color-success)'
    }
  ];

  const toggleExpand = (id) => {
    setExpandedInsight(expandedInsight === id ? null : id);
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-muted text-muted-foreground border-border'
    };
    return styles?.[priority] || styles?.low;
  };

  const getTypeBadge = (type) => {
    const labels = {
      recommendation: 'Recommendation',
      anomaly: 'Anomaly Alert',
      benchmark: 'Benchmark'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
          <h3 className="text-lg font-heading font-semibold text-card-foreground">Analytical Insights</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{insights?.length} insights</span>
          <Button variant="outline" size="sm" iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {insights?.map((insight) => (
          <div 
            key={insight?.id} 
            className="p-4 rounded-lg border border-border hover:border-primary transition-smooth cursor-pointer"
            onClick={() => toggleExpand(insight?.id)}
          >
            <div className="flex items-start gap-3 mb-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${insight?.color}15` }}
              >
                <Icon name={insight?.icon} size={18} color={insight?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded border font-medium ${getPriorityBadge(insight?.priority)}`}>
                    {insight?.priority?.toUpperCase()}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                    {getTypeBadge(insight?.type)}
                  </span>
                </div>
                <h4 className="text-sm font-semibold text-card-foreground mb-1">{insight?.title}</h4>
                <p className="text-xs text-muted-foreground">{insight?.summary}</p>
              </div>
              <Icon 
                name={expandedInsight === insight?.id ? 'ChevronUp' : 'ChevronDown'} 
                size={16} 
                color="var(--color-muted-foreground)" 
              />
            </div>

            {expandedInsight === insight?.id && (
              <div className="mt-3 pt-3 border-t border-border space-y-3 animate-fadeIn">
                <p className="text-sm text-card-foreground">{insight?.details}</p>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <Icon name="Target" size={14} color="var(--color-primary)" />
                  <span className="text-xs font-medium text-card-foreground">Recommended Action:</span>
                  <span className="text-xs text-muted-foreground">{insight?.action}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="default" size="sm" iconName="CheckCircle">
                    Take Action
                  </Button>
                  <Button variant="outline" size="sm" iconName="Archive">
                    Dismiss
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnalyticalInsightsPanel;